import client from '@/shared/apis/client'
import { AxiosPromise } from 'axios'
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'
import coreURL from '@ffmpeg/core?url'
import wasmURL from '@ffmpeg/core/wasm?url'
import { API_TIMEOUT_UPLOAD_MS } from '@/shared/apis/timeouts'

// timeout で worker が wedge した時に破棄して作り直せるよう let で保持する.
let ffmpeg = new FFmpeg()

// ffmpeg は axios でないため WASM load/exec が stall すると無限 hang する.
// 上限超過で reject し、 録音 stop / batch 送信の失敗救済導線へ倒す.
const MP3_CONVERT_TIMEOUT_MS = 120_000

/**
 * Promise を timeout 付きで待つ. ms 超過で reject する.
 * ffmpeg は AbortSignal を一律サポートしないため、 race で hang を断ち切る.
 */
const withTimeout = async <T>(
  work: Promise<T>,
  ms: number,
  label: string
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    )
  })
  try {
    return await Promise.race([work, timeout])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// ========================================
// Types
// ========================================

/**
 * 音声ソース: URL文字列またはFileオブジェクト
 */
export type AudioSource = string | File

// ========================================
// 共通処理
// ========================================

const MP3_DEBUG_TAG = '[convertToMp3]'

/**
 * 音声データをMP3形式に変換.
 * WASM の load/exec が stall した場合に無限 hang しないよう timeout で囲む.
 * timeout/失敗時は wedge した可能性のある worker を破棄して次回再 load 可能にする.
 */
export const convertToMp3 = async (audioBlob: Blob): Promise<Blob> => {
  try {
    return await withTimeout(
      convertToMp3Inner(audioBlob),
      MP3_CONVERT_TIMEOUT_MS,
      'convertToMp3'
    )
  } catch (err) {
    try {
      ffmpeg.terminate()
    } catch {
      // best effort
    }
    ffmpeg = new FFmpeg()
    throw err
  }
}

const convertToMp3Inner = async (audioBlob: Blob): Promise<Blob> => {
  console.log(
    MP3_DEBUG_TAG,
    'called: blob size=',
    audioBlob.size,
    'type=',
    audioBlob.type,
    'ffmpeg.loaded=',
    ffmpeg.loaded
  )

  if (!ffmpeg.loaded) {
    console.log(MP3_DEBUG_TAG, 'loading FFmpeg WASM...')
    const loadStart = performance.now()
    await ffmpeg.load({ coreURL, wasmURL })
    console.log(
      MP3_DEBUG_TAG,
      'FFmpeg loaded in',
      Math.round(performance.now() - loadStart),
      'ms'
    )
  }

  const inputFileName = 'input.webm'
  const outputFileName = 'output.mp3'

  // FFmpeg 仮想ファイルシステムにファイルを書き込む
  console.log(MP3_DEBUG_TAG, 'fetchFile start')
  const fetchStart = performance.now()
  const inputData = await fetchFile(audioBlob)
  console.log(
    MP3_DEBUG_TAG,
    'fetchFile done in',
    Math.round(performance.now() - fetchStart),
    'ms, inputData size=',
    inputData.byteLength
  )

  console.log(MP3_DEBUG_TAG, 'writeFile start')
  const writeStart = performance.now()
  await ffmpeg.writeFile(inputFileName, inputData)
  console.log(
    MP3_DEBUG_TAG,
    'writeFile done in',
    Math.round(performance.now() - writeStart),
    'ms'
  )

  // MP3 への変換を実行
  console.log(
    MP3_DEBUG_TAG,
    'exec start: -i',
    inputFileName,
    '-q:a 2',
    outputFileName
  )
  const execStart = performance.now()
  const exitCode = await ffmpeg.exec([
    '-i',
    inputFileName,
    '-q:a',
    '2',
    outputFileName,
  ])
  console.log(
    MP3_DEBUG_TAG,
    'exec done in',
    Math.round(performance.now() - execStart),
    'ms, exitCode=',
    exitCode
  )

  // 変換されたファイルを仮想ファイルシステムから読み込む
  console.log(MP3_DEBUG_TAG, 'readFile start')
  const readStart = performance.now()
  const data = await ffmpeg.readFile(outputFileName)
  console.log(
    MP3_DEBUG_TAG,
    'readFile done in',
    Math.round(performance.now() - readStart),
    'ms, data size=',
    data instanceof Uint8Array ? data.byteLength : (data as string).length
  )

  // Blob に変換
  const mp3Blob = new Blob([data], { type: 'audio/mp3' })
  console.log(MP3_DEBUG_TAG, 'MP3 blob created: size=', mp3Blob.size)
  return mp3Blob
}

/**
 * 音声ソースをFormDataに変換（共通処理）
 *
 * @param audioSource - 音声URL文字列またはFileオブジェクト
 * @param label - 患者識別用ラベル
 * @returns FormData（audio, label を含む）
 */
const prepareAudioFormData = async (
  audioSource: AudioSource,
  label: string,
  pharmacistId?: string,
  memoItems?: string[],
  hasCompanionOrSubstitute?: boolean
): Promise<FormData> => {
  // File の場合は直接 Blob として使用、URL の場合は fetch
  const blob =
    audioSource instanceof File
      ? audioSource
      : await fetch(audioSource).then((r) => r.blob())

  const mp3Blob = await convertToMp3(blob)
  const formData = new FormData()
  formData.append('audio', mp3Blob, 'audio.mp3')
  formData.append('label', label)
  if (pharmacistId) {
    formData.append('pharmacist_id', pharmacistId)
  }
  if (memoItems && memoItems.length > 0) {
    formData.append('memo_items', JSON.stringify(memoItems))
  }
  // 同伴/代理の別 (log/207): batch 経路の commit は stream/start を経ないため form で運ぶ。
  // undefined は送らない (= back は不変のまま)。
  if (hasCompanionOrSubstitute !== undefined) {
    formData.append(
      'has_companion_or_substitute',
      hasCompanionOrSubstitute ? 'true' : 'false'
    )
  }
  return formData
}

/**
 * 現在時刻からラベルを生成（ファイルアップロード用）
 */
export const generateTimestampLabel = (): string => {
  const now = new Date()
  const hours = now.getHours().toString().padStart(2, '0')
  const minutes = now.getMinutes().toString().padStart(2, '0')
  const seconds = now.getSeconds().toString().padStart(2, '0')
  return `${hours}${minutes}${seconds}`
}

// ========================================
// API関数
// ========================================

/**
 * 薬歴音声データの投稿（新規作成）
 *
 * @param audioSource - 音声URL文字列またはFileオブジェクト
 * @param label - 患者識別用ラベル
 */
export const postAudio = async (
  audioSource: AudioSource,
  label: string,
  pharmacistId?: string,
  memoItems?: string[]
): Promise<AxiosPromise<string>> => {
  const formData = await prepareAudioFormData(
    audioSource,
    label,
    pharmacistId,
    memoItems
  )
  return client.post(`/api/yakureki/`, formData, {
    timeout: API_TIMEOUT_UPLOAD_MS,
  })
}

/**
 * 薬歴音声イベントの更新（既存薬歴に紐づけ）
 *
 * @param audioSource - 音声URL文字列またはFileオブジェクト
 * @param label - 患者識別用ラベル
 * @param yakurekiId - 既存薬歴ID
 */
export const patchYakurekiEvent = async (
  audioSource: AudioSource,
  label: string,
  yakurekiId: string,
  pharmacistId?: string,
  memoItems?: string[],
  hasCompanionOrSubstitute?: boolean
): Promise<AxiosPromise<string>> => {
  const formData = await prepareAudioFormData(
    audioSource,
    label,
    pharmacistId,
    memoItems,
    hasCompanionOrSubstitute
  )
  return client.patch(`/api/yakureki/${yakurekiId}`, formData, {
    timeout: API_TIMEOUT_UPLOAD_MS,
  })
}
